import {useLocation, useNavigate} from 'react-router';
import {useEffect, useState} from 'react';
import menuData from '../types/menuData.json';
import MenuItem from './MenuItem.tsx';
import type {TMenuItem} from '../types';
import {CloseButton, FullScreen, FullScreenIcon, Label} from '../types/MenuItem.styled.ts';
import FullScreenSVG from '../../../assets/broadcast/scale-expanding.svg';
import {LayoutButton, LayoutItem} from '../../../pages/stream/types/Streaming.styled.ts';
import FirstActiveSVG from '../../../assets/layout-icon/pip_select.svg';
import FirstSVG from '../../../assets/layout-icon/pip.svg';
import SecondActiveSVG from '../../../assets/layout-icon/4_select.svg';
import SecondSVG from '../../../assets/layout-icon/4.svg';
import ThirdActiveSVG from '../../../assets/layout-icon/16_select.svg';
import ThirdSVG from '../../../assets/layout-icon/16.svg';
import AutoActiveSVG from '../../../assets/layout-icon/26_select.svg';
import AutoSVG from '../../../assets/layout-icon/26.svg';
import useStreamingStore from '../../../store/useStreamingStore.ts';
import useGlobalStore from '../../../store/useGlobalStore.ts';
import {setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';

const MenuList = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const getCurrentMenu = () => {
        const path = location.pathname.slice(1);
        return path || 'streaming';
    };
    const activeMenu = getCurrentMenu();

    useEffect(() => {
        if (location.pathname === '/') {
            navigate('/streaming');
        }
    }, [location.pathname, navigate]);

    const {streamingPollingData, setGridLayout} = useStreamingStore();
    const {isFullScreen, setFullScreen} = useGlobalStore();
    const [showScreenIcons, setShowScreenIcons] = useState(true);
    const [selectedLayout, setSelectedLayout] = useState<string>('auto');

    const {ref: fullIconRef, focused: fullIconFocused} = useFocusable({
        focusKey: 'streaming-page-full-icon',
        onEnterPress: () => {
            setFullScreen(!isFullScreen);
            showControlsTemporarily();
        },
        onArrowPress: (direction) => {
            if (isFullScreen) {
                showControlsTemporarily();
                return false;
            }
            if (direction === 'up') {
                setFocus('replay');
                return false;
            } else if (direction === 'down') {
                if (streamingPollingData && streamingPollingData.broadcast.content_type === 'live') {
                    setFocus('firstLayout');
                    return false;
                }
                return false;
            }
            return false;
        },
    });
    const {ref: firstLayoutRef, focused: firstLayoutFocused} = useFocusable({
        focusKey: 'firstLayout',
        onEnterPress: () => {
            setSelectedLayout('first');
            setGridLayout({mode: 'pip', sub: true});
        },
        onArrowPress: (direction) => {
            if (direction === 'up') {
                setFocus('streaming-page-full-icon');
                return false;
            }
            return true;
        },
    });
    const {ref: secondLayoutRef, focused: secondLayoutFocused} = useFocusable({
        focusKey: 'secondLayout',
        onEnterPress: () => {
            setSelectedLayout('second');
            setGridLayout({mode: '4', sub: true});
        },
        onArrowPress: (direction) => {
            if (direction === 'up') {
                setFocus('streaming-page-full-icon');
                return false;
            }
            return true;
        },
    });
    const {ref: thirdLayoutRef, focused: thirdLayoutFocused} = useFocusable({
        focusKey: 'thirdLayout',
        onEnterPress: () => {
            setSelectedLayout('third');
            setGridLayout({mode: '22', sub: true});
        },
        onArrowPress: (direction) => {
            if (direction === 'up') {
                setFocus('streaming-page-full-icon');
                return false;
            }
            return true;
        },
    });
    const {ref: autoLayoutRef, focused: autoLayoutFocused} = useFocusable({
        focusKey: 'autoLayout',
        onEnterPress: () => {
            setSelectedLayout('auto');
            setGridLayout({mode: '28', sub: true});
        },
        onArrowPress: (direction) => {
            if (direction === 'up') {
                setFocus('streaming-page-full-icon');
                return false;
            }
            return true;
        },
    });

    const [closeVisible, setCloseVisible] = useState<boolean>(false);
    const [hideControlsTimeout, setHideControlsTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

    const showControlsTemporarily = () => {
        setCloseVisible(true);
        if (hideControlsTimeout) {
            clearTimeout(hideControlsTimeout);
        }
        const timeout = setTimeout(() => {
            setCloseVisible(false);
        }, 3000);
        setHideControlsTimeout(timeout);
    };

    return (
        <>
            {menuData.map((item) => (
                <MenuItem key={item.name} item={item as TMenuItem} isActive={activeMenu === item.name} />
            ))}

            <FullScreen
                ref={fullIconRef}
                $focused={fullIconFocused}
                $disable={!(location.pathname === '/streaming' || location.pathname === '/iptv')}
            >
                <FullScreenIcon src={FullScreenSVG} alt="전체화면" $focused={fullIconFocused} />
                <Label $focused={fullIconFocused}>전체화면</Label>
            </FullScreen>

            <LayoutButton
                $show={showScreenIcons}
                $isFullScreen={isFullScreen}
                $disable={
                    !(
                        location.pathname === '/streaming' &&
                        streamingPollingData &&
                        streamingPollingData.broadcast.content_type === 'live'
                    )
                }
            >
                <LayoutItem
                    ref={firstLayoutRef}
                    $focused={firstLayoutFocused}
                    onClick={() => setGridLayout({mode: 'pip', sub: true})}
                >
                    <img src={selectedLayout === 'first' ? FirstActiveSVG : FirstSVG} alt="first-layout" />
                </LayoutItem>
                <LayoutItem
                    ref={secondLayoutRef}
                    $focused={secondLayoutFocused}
                    onClick={() => setGridLayout({mode: '4', sub: true})}
                >
                    <img src={selectedLayout === 'second' ? SecondActiveSVG : SecondSVG} alt="second-layout" />
                </LayoutItem>
                <LayoutItem
                    ref={thirdLayoutRef}
                    $focused={thirdLayoutFocused}
                    onClick={() => setGridLayout({mode: '22', sub: true})}
                >
                    <img src={selectedLayout === 'third' ? ThirdActiveSVG : ThirdSVG} alt="third-layout" />
                </LayoutItem>
                <LayoutItem
                    ref={autoLayoutRef}
                    $focused={autoLayoutFocused}
                    onClick={() => setGridLayout({mode: '28', sub: true})}
                >
                    <img src={selectedLayout === 'auto' ? AutoActiveSVG : AutoSVG} alt="auto-layout" />
                </LayoutItem>
            </LayoutButton>

            {isFullScreen && (
                <CloseButton
                    ref={fullIconRef}
                    $focused={fullIconFocused}
                    $visible={closeVisible}
                    onClick={() => setFullScreen(false)}
                >
                    전체 화면 닫기
                </CloseButton>
            )}
        </>
    );
};

export default MenuList;
